import { useReducer } from 'react';
import addPathPrefix from 'utils/addPathPrefix';
import NewFormCard, { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import { DeclarationFormCardProps } from 'views/declarations/common/declaration.form.card';
import PlaceSection from './PlaceSection';

export type PlaceSectionKey = 'placeOfLoading' | 'placeOfUnloading' | 'placeOfAcceptance' | 'placeOfDelivery';

type Props = {
    path?: string;
    sections?: PlaceSectionKey[];
} & DeclarationFormCardProps;

type ExpandedState = Record<PlaceSectionKey, boolean>;

type Action = {
    type: 'TOGGLE';
    key: PlaceSectionKey;
};

type Section = {
    key: PlaceSectionKey;
    fieldPrefix: string;
    countryLabel: string;
    locationLabel: string;
    unLocodeRefNumber: string;
    countryRefNumber: string;
    locationRefNumber: string;
    title: string;
    required: boolean;
};

const initialState: ExpandedState = {
    placeOfLoading: true,
    placeOfUnloading: true,
    placeOfAcceptance: true,
    placeOfDelivery: true,
};

const reducer = (state: ExpandedState, action: Action): ExpandedState => {
    switch (action.type) {
        case 'TOGGLE':
            return {
                ...state,
                [action.key]: !state[action.key],
            };
        default:
            return state;
    }
};

const placeSections: Section[] = [
    {
        key: 'placeOfLoading',
        fieldPrefix: 'placeOfLoading',
        countryLabel: 'Place of Loading Country',
        locationLabel: 'Place of Loading Location',
        unLocodeRefNumber: '16 13 036 000',
        countryRefNumber: '16 13 020 000',
        locationRefNumber: '16 13 037 000',
        title: 'Place of Loading',
        required: true,
    },
    {
        key: 'placeOfUnloading',
        fieldPrefix: 'placeOfUnloading',
        countryLabel: 'Place of Unloading Country',
        locationLabel: 'Place of Unloading Location',
        unLocodeRefNumber: '16 14 036 000',
        countryRefNumber: '16 13 020 000',
        locationRefNumber: '16 13 037 000',
        title: 'Place of Unloading',
        required: true,
    },
    {
        key: 'placeOfAcceptance',
        fieldPrefix: 'placeOfAcceptance',
        countryLabel: 'Place of Acceptance Country',
        locationLabel: 'Place of Acceptance Location',
        unLocodeRefNumber: '16 16 036 000',
        countryRefNumber: '16 13 020 000',
        locationRefNumber: '16 13 037 000',
        title: 'Place of Acceptance',
        required: true,
    },
    {
        key: 'placeOfDelivery',
        fieldPrefix: 'placeOfDelivery',
        countryLabel: 'Place of Delivery Country',
        locationLabel: 'Place of Delivery Location',
        unLocodeRefNumber: '16 05 036 000',
        countryRefNumber: '16 13 020 000',
        locationRefNumber: '16 13 037 000',
        title: 'Place of Delivery',
        required: true,
    },
];

const PlacesCard = ({ formik, path, sections }: Props) => {
    const [expanded, dispatch] = useReducer(reducer, initialState);

    if (!formik) return null;

    const visibleSections = sections ? placeSections.filter(({ key }) => sections.includes(key)) : placeSections;

    return (
        <NewFormCard title="Places">
            <FormCardContainer>
                {visibleSections.map((section) => (
                    <PlaceSection
                        key={section.key}
                        formik={formik}
                        title={section.title}
                        required={section.required}
                        expanded={expanded[section.key]}
                        onToggle={() => dispatch({ type: 'TOGGLE', key: section.key })}
                        fieldPrefix={addPathPrefix(path, section.fieldPrefix)}
                        countryLabel={section.countryLabel}
                        locationLabel={section.locationLabel}
                        unLocodeRefNumber={section.unLocodeRefNumber}
                        countryRefNumber={section.countryRefNumber}
                        locationRefNumber={section.locationRefNumber}
                    />
                ))}
            </FormCardContainer>
        </NewFormCard>
    );
};

export default PlacesCard;
